import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Search } from "lucide-react";
import { formatINR } from "@/lib/constants";
import { useAuth } from "@/lib/auth";

/**
 * Stage-2 Held Funds tab · what the client actually holds today.
 * Feeds the Portfolio Overlap and Underperformance tools.
 */
export default function HeldFundsTab({ clientId, canEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [held, setHeld] = useState([]);
  const [dbFunds, setDbFunds] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [source, setSource] = useState("From Our Research DB");
  const [q, setQ] = useState("");
  const [f, setF] = useState(blankExternal());

  const load = async () => {
    const { data } = await api.get(`/clients/${clientId}/held-funds`);
    setHeld(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => {
    if (!isAdmin || source !== "From Our Research DB") return;
    (async () => {
      try {
        const { data } = await api.get("/mutual-funds");
        setDbFunds(data);
      } catch { setDbFunds([]); }
    })();
  }, [isAdmin, source]);

  const addFromDb = async (fund) => {
    try {
      await api.post(`/clients/${clientId}/held-funds`, {
        fund_source: "From Our Research DB",
        mutual_fund_id: fund.id,
        amount_invested: 0,
      });
      toast.success(`Added ${fund.name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const addExternal = async () => {
    if (!f.external_fund_name) { toast.error("Fund name required"); return; }
    try {
      await api.post(`/clients/${clientId}/held-funds`, {
        fund_source: "External / Manual Entry",
        external_fund_name: f.external_fund_name,
        external_category: f.external_category,
        external_sub_category: f.external_sub_category,
        amount_invested: Number(f.amount_invested) || 0,
        current_value: Number(f.current_value) || 0,
        market_cap_allocation: {
          large_pct: Number(f.large_pct) || 0,
          mid_pct: Number(f.mid_pct) || 0,
          small_pct: Number(f.small_pct) || 0,
        },
        sector_allocation: {
          IT_pct: Number(f.IT_pct) || 0,
          Banking_pct: Number(f.Banking_pct) || 0,
          NBFC_pct: Number(f.NBFC_pct) || 0,
          Metals_pct: Number(f.Metals_pct) || 0,
          FMCG_pct: Number(f.FMCG_pct) || 0,
          Auto_Ancillaries_pct: Number(f.Auto_Ancillaries_pct) || 0,
          Finance_pct: Number(f.Finance_pct) || 0,
          Insurance_pct: Number(f.Insurance_pct) || 0,
          Other_pct: Number(f.Other_pct) || 0,
        },
        trailing_returns: {
          return_1yr_pct: f.return_1yr_pct === "" ? null : Number(f.return_1yr_pct),
          return_3yr_pct: f.return_3yr_pct === "" ? null : Number(f.return_3yr_pct),
          return_5yr_pct: f.return_5yr_pct === "" ? null : Number(f.return_5yr_pct),
        },
      });
      toast.success("Added");
      setDlg(false);
      setF(blankExternal());
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateAmount = async (h, amt) => {
    try {
      await api.patch(`/held-funds/${h.id}`, { amount_invested: Number(amt) || 0 });
      load();
    } catch { /* silent */ }
  };

  const remove = async (h) => {
    if (!window.confirm(`Remove "${h.external_fund_name}"?`)) return;
    try {
      await api.delete(`/held-funds/${h.id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Failed"); }
  };

  const filteredDb = dbFunds.filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline mb-1 text-primary">Current holdings</p>
          <p className="text-sm text-muted-foreground">
            What the client actually holds today — feeds the Overlap and Underperformance tools.
          </p>
        </div>
        {canEdit && (
          <Dialog open={dlg} onOpenChange={setDlg}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-held-fund-btn"><Plus className="mr-1 h-3 w-3" /> Add held fund</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add held fund</DialogTitle></DialogHeader>
              <div className="space-y-4 py-3">
                <div className="flex gap-2 rounded-md border border-border p-1">
                  {isAdmin && (
                    <button
                      onClick={() => setSource("From Our Research DB")}
                      className={`flex-1 rounded-md px-3 py-2 text-sm ${source === "From Our Research DB" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                      data-testid="held-source-db"
                    >
                      From Research DB
                    </button>
                  )}
                  <button
                    onClick={() => setSource("External / Manual Entry")}
                    className={`flex-1 rounded-md px-3 py-2 text-sm ${source === "External / Manual Entry" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                    data-testid="held-source-external"
                  >
                    Manual entry
                  </button>
                </div>

                {source === "From Our Research DB" && isAdmin && (
                  <div>
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input placeholder="Search fund…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="held-db-search" />
                    </div>
                    <div className="max-h-72 space-y-1 overflow-y-auto">
                      {filteredDb.length === 0 && <p className="text-sm text-muted-foreground">No funds.</p>}
                      {filteredDb.map((fund) => (
                        <button
                          key={fund.id}
                          onClick={() => addFromDb(fund)}
                          className="flex w-full items-center justify-between rounded-md border border-border bg-white px-3 py-2 text-left text-sm hover:bg-secondary/40"
                          data-testid={`held-db-pick-${fund.id}`}
                        >
                          <span>
                            <span className="font-medium">{fund.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground">{fund.sub_category}</span>
                          </span>
                          <Plus className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {source === "External / Manual Entry" && (
                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div><Label>Fund name</Label><Input value={f.external_fund_name} onChange={(e) => setF({ ...f, external_fund_name: e.target.value })} data-testid="held-ext-name" /></div>
                      <div><Label>Sub-category</Label><Input value={f.external_sub_category} onChange={(e) => setF({ ...f, external_sub_category: e.target.value })} placeholder="e.g. Flexi Cap" data-testid="held-ext-subcat" /></div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div><Label>Amount invested (₹)</Label><Input type="number" value={f.amount_invested} onChange={(e) => setF({ ...f, amount_invested: e.target.value })} data-testid="held-ext-amount" /></div>
                      <div><Label>Current value (₹)</Label><Input type="number" value={f.current_value} onChange={(e) => setF({ ...f, current_value: e.target.value })} data-testid="held-ext-value" /></div>
                    </div>
                    <div>
                      <div className="overline mb-2 text-muted-foreground">Market cap allocation</div>
                      <div className="grid gap-2 md:grid-cols-3">
                        <div><Label className="text-xs">Large %</Label><Input type="number" value={f.large_pct} onChange={(e) => setF({ ...f, large_pct: e.target.value })} data-testid="held-ext-large" /></div>
                        <div><Label className="text-xs">Mid %</Label><Input type="number" value={f.mid_pct} onChange={(e) => setF({ ...f, mid_pct: e.target.value })} data-testid="held-ext-mid" /></div>
                        <div><Label className="text-xs">Small %</Label><Input type="number" value={f.small_pct} onChange={(e) => setF({ ...f, small_pct: e.target.value })} data-testid="held-ext-small" /></div>
                      </div>
                    </div>
                    <div>
                      <div className="overline mb-2 text-muted-foreground">Sector allocation (%)</div>
                      <div className="grid gap-2 md:grid-cols-3">
                        {["IT_pct","Banking_pct","NBFC_pct","Metals_pct","FMCG_pct","Auto_Ancillaries_pct","Finance_pct","Insurance_pct","Other_pct"].map((k) => (
                          <div key={k}>
                            <Label className="text-xs">{k.replace("_pct", "").replace(/_/g, " ")}</Label>
                            <Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`held-ext-${k}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="overline mb-2 text-muted-foreground">Trailing returns (%)</div>
                      <div className="grid gap-2 md:grid-cols-3">
                        <div><Label className="text-xs">1Y</Label><Input type="number" step="0.01" value={f.return_1yr_pct} onChange={(e) => setF({ ...f, return_1yr_pct: e.target.value })} data-testid="held-ext-r1" /></div>
                        <div><Label className="text-xs">3Y</Label><Input type="number" step="0.01" value={f.return_3yr_pct} onChange={(e) => setF({ ...f, return_3yr_pct: e.target.value })} data-testid="held-ext-r3" /></div>
                        <div><Label className="text-xs">5Y</Label><Input type="number" step="0.01" value={f.return_5yr_pct} onChange={(e) => setF({ ...f, return_5yr_pct: e.target.value })} data-testid="held-ext-r5" /></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {source === "External / Manual Entry" && (
                  <Button onClick={addExternal} data-testid="held-ext-save">Add fund</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border-border bg-white p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Fund</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3 text-right">Amount invested</th>
              <th className="px-4 py-3 text-right">Current value</th>
              {canEdit && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {held.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                No held funds recorded yet.
              </td></tr>
            )}
            {held.map((h) => (
              <tr key={h.id} data-testid={`held-row-${h.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{h.external_fund_name}</div>
                  <div className="text-xs text-muted-foreground">{h.external_sub_category || "—"}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className={h.fund_source === "From Our Research DB" ? "text-primary" : "text-muted-foreground"}>
                    {h.fund_source}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {canEdit ? (
                    <Input
                      type="number"
                      defaultValue={h.amount_invested || 0}
                      onBlur={(e) => updateAmount(h, e.target.value)}
                      className="ml-auto h-8 w-32 tabular-nums text-right text-xs"
                      data-testid={`held-amount-${h.id}`}
                    />
                  ) : (
                    <span className="tabular-nums">{formatINR(h.amount_invested)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatINR(h.current_value)}</td>
                {canEdit && (
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(h)} data-testid={`held-remove-${h.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function blankExternal() {
  return {
    external_fund_name: "", external_category: "", external_sub_category: "",
    amount_invested: 0, current_value: 0,
    large_pct: 0, mid_pct: 0, small_pct: 0,
    IT_pct: 0, Banking_pct: 0, NBFC_pct: 0, Metals_pct: 0, FMCG_pct: 0,
    Auto_Ancillaries_pct: 0, Finance_pct: 0, Insurance_pct: 0, Other_pct: 0,
    return_1yr_pct: "", return_3yr_pct: "", return_5yr_pct: "",
  };
}
